/**
 * Utility functions for processing SSE streams
 */

/**
 * Check if a string is a plain text error message
 * @param {string} text - Text to check
 * @returns {boolean} True if text appears to be an error message
 */
// TODO: This is a temporary function to check errors and display them in the chat for testing purposes.
// We should get consensus if we want to keep this function or not.
function isPlainTextError(text) {
  if (!text || typeof text !== 'string') return false;
  
  // Check for common error patterns
  const errorPatterns = [
    /^Error while running agent:/i,
    /^Error \d{3}/i,
    /Resource exhausted/i,
    /RESOURCE_EXHAUSTED/i,
    /failed to call model:/i,
    /Please try again later/i
  ];
  
  return errorPatterns.some(pattern => pattern.test(text));
}

/**
 * Extract error code from plain text error message
 * @param {string} text - Error message text
 * @returns {string|null} Error code if found, null otherwise
 */
function extractErrorCodeFromText(text) {
  if (!text) return null;
  
  // Extract 429 from "Error 429"
  const error429Match = text.match(/Error (\d{3})/i);
  if (error429Match) {
    return error429Match[1];
  }
  
  // Check for RESOURCE_EXHAUSTED
  if (text.includes('RESOURCE_EXHAUSTED') || text.includes('Resource exhausted')) {
    return '429';
  }
  
  return null;
}

/**
 * Process SSE response and handle events
 * @param {Response} response - Fetch response object
 * @param {Object} handlers - Event handlers and state setters
 * @param {Function} handlers.processSSEEvent - Function to process individual SSE events
 * @param {Function} handlers.setIsStreaming - Function to set streaming state
 * @param {Object} handlers.allEventsRef - Ref to track all events for debugging
 * @param {Function} handlers.onStreamStart - Optional callback when stream starts (first event received)
 * @param {Function} handlers.onStreamEnd - Optional callback when stream ends
 */
export async function processSSEStream(response, { processSSEEvent, setIsStreaming, allEventsRef, onStreamStart, onStreamEnd }) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let streamActive = true; // Track if stream connection is open

  try {
    if (onStreamStart) {
      onStreamStart();
    }

    while (streamActive) {
      const { done, value } = await reader.read();
      if (done) {
        streamActive = false;
        if (onStreamEnd) {
          onStreamEnd();
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.trim() === '') continue;

        if (line.startsWith('data: ')) {
          try {
            const dataStr = line.slice(6).trim();
            
            // Log raw SSE data
            console.log('📥 Raw SSE data:', dataStr);
            
            if (dataStr === '[DONE]') {
              streamActive = false;
              if (onStreamEnd) {
                onStreamEnd();
              }
              break;
            }

            // Check if this is a plain text error message (not JSON)
            if (isPlainTextError(dataStr)) {
              console.log('Detected plain text error:', dataStr);
              // Create error event from plain text
              const errorEvent = {
                error: dataStr,
                errorCode: extractErrorCodeFromText(dataStr),
                status: dataStr.includes('RESOURCE_EXHAUSTED') ? 'RESOURCE_EXHAUSTED' : null,
                timestamp: Date.now()
              };
              console.log('Created error event:', errorEvent);
              processSSEEvent(errorEvent);
              continue;
            }

            const rawData = JSON.parse(dataStr);
            console.log('Parsed SSE JSON:', rawData);

            // Process the event
            processSSEEvent(rawData);
          } catch (e) {
            // If JSON parsing fails, check if it's an error message
            const dataStr = line.slice(6).trim();
            console.log('JSON parse failed, checking if error:', dataStr);
            if (isPlainTextError(dataStr)) {
              console.log('Detected error in catch block:', dataStr);
              const errorEvent = {
                error: dataStr,
                errorCode: extractErrorCodeFromText(dataStr),
                status: dataStr.includes('RESOURCE_EXHAUSTED') ? 'RESOURCE_EXHAUSTED' : null,
                timestamp: Date.now()
              };
              console.log('Created error event in catch:', errorEvent);
              processSSEEvent(errorEvent);
            } else {
              // Ignore other JSON parse errors for non-JSON lines
              console.debug('Failed to parse SSE data:', e, line);
            }
          }
        } else if (line.startsWith('event: ')) {
          // Handle SSE event types
          const eventType = line.slice(7).trim();
          if (eventType === 'end') {
            streamActive = false;
            if (onStreamEnd) {
              onStreamEnd();
            }
            break;
          } else if (eventType === 'error') {
            streamActive = false;
            if (onStreamEnd) {
              onStreamEnd();
            }
            break;
          }
        }
      }
    }
  } catch (error) {
    console.error('Error processing SSE stream:', error);
    streamActive = false;
    // Re-throw to be handled by the caller
    throw error;
  } finally {
    // Ensure streaming state is cleared and stream is marked as ended
    if (streamActive) {
      streamActive = false;
    }
    if (setIsStreaming) {
      setIsStreaming(false);
    }
    if (onStreamEnd) {
      onStreamEnd();
    }
    try {
      reader.releaseLock();
    } catch (e) {
      // Reader may already be released
    }
  }
}
